import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { AuthLayout, AuthLink } from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", {
        email: email.trim(),
        tenant_slug: tenantSlug.trim() || undefined,
      });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email and we'll send you a magic link."
      footer={<>Remembered it? <AuthLink to="/login">Sign in</AuthLink></>}
    >
      {sent ? (
        <div className="rounded-xl border bg-accent/50 p-6 text-center" data-testid="forgot-sent">
          <MailCheck className="mx-auto mb-3 h-10 w-10 text-secondary" />
          <div className="font-display text-lg font-semibold text-primary">Check your inbox</div>
          <p className="mt-1 text-sm text-muted-foreground">
            If that email exists in our system, you'll receive a reset link within a minute. The link expires in 1 hour.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
          <div className="space-y-2">
            <Label htmlFor="email">Work email</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant_slug">Workspace URL <span className="text-muted-foreground">(optional)</span></Label>
            <Input id="tenant_slug" value={tenantSlug} onChange={(e) => setTenantSlug(e.target.value)} placeholder="acme" data-testid="forgot-tenant" />
          </div>
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
